import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/ui/PublicHeader';
import Icon from '../../components/AppIcon';
import GenderSelectionCard from './components/GenderSelectionCard';
import RegistrationForm from './components/RegistrationForm';
import LoginForm from './components/LoginForm';
import OTPVerificationModal from './components/OTPVerificationModal';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import TrustSignals from './components/TrustSignals';
import { useAuth } from '../../contexts/AuthContext';
import { useEffect } from 'react';

const RegistrationLogin = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('register');
  const [selectedGender, setSelectedGender] = useState(null);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate('/member-dashboard');
    }
  }, [user, loading, navigate]);

  const [femaleFeatures, setFemaleFeatures] = useState([
    'Completely free access',
    'Unlimited messaging',
    'Advanced privacy controls',
    'Priority profile visibility',
    'All premium features included'
  ]);

  const [maleFeatures, setMaleFeatures] = useState([
    'Unlimited messaging',
    'Advanced search filters',
    'Read receipts',
    'Priority support',
    'Enhanced profile visibility'
  ]);

  const handleGenderSelect = (gender) => {
    setSelectedGender(gender);
  };

  const handleRegistrationSubmit = async (formData) => {
    setRegistrationData(formData);
    
    // Check if user is authenticated (may need to wait for auth state to update)
    // If email confirmation is required, user might not be authenticated yet
    const checkAuthAndNavigate = () => {
      // Wait a moment for auth state to update after registration
      setTimeout(() => {
        if (user) {
          // User is authenticated, navigate to profile creation
          navigate('/profile-creation', { 
            state: { 
              gender: selectedGender,
              ...formData 
            } 
          });
        } else {
          // User not authenticated yet (likely email confirmation required)
          // Show message and allow manual navigation after email confirmation
          alert('Registration successful! Please check your email to confirm your account, then sign in to continue.');
          setActiveTab('login');
        }
      }, 500);
    };
    
    checkAuthAndNavigate();
  };

  const handleOTPVerify = (otp) => {
    setShowOTPModal(false);
    navigate('/profile-creation', { 
      state: { 
        gender: selectedGender,
        ...registrationData 
      } 
    });
  };

  const handleOTPResend = () => {
    console.log('OTP resent');
  };

  const handleLoginSubmit = (formData) => {
    // Login handled by LoginForm component
    // AuthContext will redirect to dashboard on success
  };

  const handleForgotPasswordSubmit = (data) => {
    setShowForgotPassword(false);
    alert(`Password reset link sent to ${data?.identifier}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      
      <div className="mx-auto max-w-[1200px] px-4 py-8 lg:px-6 lg:py-12">
        <div className="mb-8 text-center">
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Your discreet platform for meaningful connections. Join thousands of adults seeking private relationships.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-xl shadow-warm-lg p-6 md:p-8 mb-6">
            <div className="flex gap-2 p-1 bg-muted rounded-lg mb-6">
              <button
                onClick={() => setActiveTab('register')}
                className={`flex-1 px-4 py-3 rounded-md text-sm md:text-base font-medium transition-smooth ${
                  activeTab === 'register' ?'bg-card text-foreground shadow-warm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="UserPlus" size={18} className="inline mr-2" />
                Create Account
              </button>
              <button
                onClick={() => setActiveTab('login')}
                className={`flex-1 px-4 py-3 rounded-md text-sm md:text-base font-medium transition-smooth ${
                  activeTab === 'login' ?'bg-card text-foreground shadow-warm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="LogIn" size={18} className="inline mr-2" />
                Sign In
              </button>
            </div>

            {activeTab === 'register' ? (
              <div className="space-y-6">
                {!selectedGender ? (
                  <>
                    <div className="text-center mb-6">
                      <h2 className="text-xl md:text-2xl font-heading font-semibold mb-2">Choose Your Account Type</h2>
                      <p className="text-sm text-muted-foreground">Select your gender to get started</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <GenderSelectionCard
                        gender="female"
                        title="Female Account"
                        price="FREE"
                        features={femaleFeatures}
                        isSelected={selectedGender === 'female'}
                        onClick={() => handleGenderSelect('female')}
                      />
                      <GenderSelectionCard
                        gender="male"
                        title="Male Account"
                        price="₹2,500"
                        features={maleFeatures}
                        isSelected={selectedGender === 'male'}
                        onClick={() => handleGenderSelect('male')}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl md:text-2xl font-heading font-semibold mb-1">Complete Your Registration</h2>
                        <p className="text-sm text-muted-foreground">
                          {selectedGender === 'female' ? 'Free account' : '₹2,500/month subscription'}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedGender(null)}
                        className="flex items-center gap-2 text-sm text-primary hover:underline font-medium"
                      >
                        <Icon name="ArrowLeft" size={16} />
                        Change
                      </button>
                    </div>

                    <RegistrationForm
                      selectedGender={selectedGender}
                      onSubmit={handleRegistrationSubmit}
                      onSwitchToLogin={() => setActiveTab('login')}
                    />
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl md:text-2xl font-heading font-semibold mb-2">Welcome Back</h2>
                  <p className="text-sm text-muted-foreground">Sign in to continue your journey</p>
                </div>

                <LoginForm
                  onSubmit={handleLoginSubmit}
                  onSwitchToRegister={() => {
                    setActiveTab('register');
                    setSelectedGender(null);
                  }}
                  onForgotPassword={() => setShowForgotPassword(true)}
                />
              </div>
            )}
          </div>

          <TrustSignals />

          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground mb-2">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
            <div className="flex items-center justify-center gap-4 text-xs">
              <button
                onClick={() => navigate('/home-landing')}
                className="text-primary hover:underline font-medium"
              >
                Back to Home
              </button>
              <span className="text-muted-foreground">•</span>
              <a href="#" className="text-primary hover:underline font-medium">
                Privacy Policy
              </a>
              <span className="text-muted-foreground">•</span>
              <a href="#" className="text-primary hover:underline font-medium">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
      {showOTPModal && registrationData && (
        <OTPVerificationModal
          contactValue={registrationData?.email}
          onVerify={handleOTPVerify}
          onClose={() => setShowOTPModal(false)}
          onResend={handleOTPResend}
        />
      )}
      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
          onSubmit={handleForgotPasswordSubmit}
        />
      )}
    </div>
  );
};

export default RegistrationLogin;